import BadgeCheckSVG from "@/assets/svg/badge-check.svg";
import YoutubeIconSVG from "@/assets/svg/youtube-icon.svg";
import {
  EnfinitySecondaryBtn
} from "@/components/design-system/enfinity/EnfinityButton";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Header() {
  const { ref, isVisible } = useScrollReveal();
  const navigate = useNavigate();
  const [showRest, setShowRest] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowRest(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const restVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 45,
        damping: 8,
        mass: 1.2,
        duration: 2,
      },
    },
  };

  const handleHowItWorksClick = () => {
    // Navigate to main page
    navigate("/");

    // Wait for route change, then scroll to CreatorsLifeCycle
    setTimeout(() => {
      const scrollToSection = (attempts = 0) => {
        if (attempts > 15) return; // Max 15 attempts (1.5 seconds)

        const section = document.getElementById("start-earning");
        if (section) {
          section.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        } else {
          setTimeout(() => scrollToSection(attempts + 1), 100);
        }
      };
      scrollToSection();
    }, 200);
  };

  const badge = {
    name: "Official YouTube Certified",
    icon: YoutubeIconSVG,
    url: "https://servicesdirectory.withyoutube.com/directory/enfinity-talent-agency/",
  };

  return (
    <div
      id="header"
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } py-20 xxs:py-16 xl:pt-28 1xl:pt-40 xxs:pt-24 lg:py-16 xl:py-[30px] 1xl:py-[40px] 2xl:py-32 lg:pt-28 4xl:py-[100px] 4xl:pt-28 px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-12 xl:px-12 4xl:px-16 relative bg-[#1a1a1a]`}
    >
      {/* Background Image - Mobile */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-no-repeat z-0 lg:hidden"
        style={{
          backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/pay-bg-mobile.png')`,
          backgroundPosition: "center bottom",
        }}
      />

      {/* Background Image - Desktop */}
      <div
        className="hidden lg:block absolute inset-0 w-full h-full bg-cover bg-no-repeat z-0"
        style={{
          backgroundImage: `url('https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/pay-bg.png')`,
          backgroundPosition: "center bottom",
        }}
      />

      {/* Content Overlay */}
      <div className="relative z-10">
        <div className="mx-auto flex flex-col items-center gap-[30px] relative">
          {/* Badge */}
          <div className="flex justify-center">
            <a href={badge.url} target="_blank" rel="noopener noreferrer">
              <div
                className="flex items-center gap-2 rounded-full py-1 px-4
             border border-white/20 bg-white/5 backdrop-blur-md text-white shimmer-badge"
              >
                <img src={badge.icon} alt={badge.name} className="h-8 w-8" />
                <span>{badge.name}</span>
                <img src={BadgeCheckSVG} className="h-5 w-5" alt="Badge" />
              </div>
            </a>
          </div>

          {/* Trusted by Section */}
          <div className="flex flex-col lg:flex-row items-center justify-center gap-2 lg:gap-[10px] rounded-[100px] h-auto lg:h-[43px]"></div>

          {/* Main Heading */}
          <h1 className="text-5xl xxs:text-[55px] lg:text-[80px] 4xl:text-[90px] font-normal text-white text-center xxs:leading-tight xs:leading-tight sm:leading-tight md:leading-tight lg:leading-tight enfinity-header-font -mt-8 lg:-mt-14">
            <span>Unlock </span>
            <span className="relative inline-block">
              {/* Stroke layer with vertical gradient mask - behind fill */}
              <span
                className="absolute inset-0 text-white z-0"
                style={{
                  WebkitTextStroke: "2px white",
                  WebkitTextFillColor: "transparent",
                  maskImage:
                    "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  pointerEvents: "none",
                }}
              >
                flexible payouts,
              </span>
              {/* Fill layer with original color - on top */}
              <span className="relative z-10 text-[#00A656]">
                flexible payouts,
              </span>
            </span>
            <br />
            <span className="relative inline-block">
              {/* Stroke layer with vertical gradient mask - behind fill */}
              <span
                className="absolute inset-0 text-white z-0"
                style={{
                  WebkitTextStroke: "2px white",
                  WebkitTextFillColor: "transparent",
                  maskImage:
                    "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)",
                  pointerEvents: "none",
                }}
              >
                crypto{" "}
              </span>
              {/* Fill layer with original color - on top */}
              <span className="relative z-10 text-[#00A656]">crypto </span>
            </span>{" "}
            <span>withdrawals</span>
          </h1>

          {/* Subtitle */}
          <p className="text-xl xxs:text-[1rem] lg:text-lg font-normal text-white text-center max-w-4xl leading-[30px] enfinity-subtext-font">
            Take control of your earnings with flexible payout options built for
            creators. Get paid faster with daily payouts or withdraw in crypto.
            Access your revenue when you want, your way.
          </p>

          {/* Stats Container */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 py-6 px-8 rounded-[20px] w-full max-w-[760px]">
            <div className="flex flex-col gap-[5px] items-center">
              <p className="text-[62px] xxs:text-[55px] font-normal text-[#00A656] leading-normal enfinity-header-font">
                $75M+
              </p>
              <p className="text-lg xxs:text-[1rem] lg:text-lg 4xl:text-[18px] font-normal text-white enfinity-subtext-font">
                Revenue Recouped
              </p>
            </div>
            <div className="flex flex-col gap-[5px] items-center">
              <p className="text-[62px] xxs:text-[55px] font-normal text-[#00A656] leading-normal enfinity-header-font">
                4,500+
              </p>
              <p className="text-lg xxs:text-[1rem] lg:text-lg 4xl:text-[18px] font-normal text-white enfinity-subtext-font">
                Active Creators
              </p>
            </div>
            <div className="flex flex-col gap-[5px] items-center">
              <p className="text-[62px] xxs:text-[55px] font-normal text-[#00A656] leading-normal enfinity-header-font">
                Daily
              </p>
              <p className="text-lg xxs:text-[1rem] lg:text-lg 4xl:text-[18px] font-normal text-white enfinity-subtext-font">
                Payout Frequency
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-[10px] w-full sm:w-full md:w-auto z-10"></div>
        </div>

        {/* Button */}
        <motion.div
          variants={restVariants}
          initial="hidden"
          animate={showRest ? "visible" : "hidden"}
          className=""
        >
          <a
            href="https://core.enfinity.com/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-[-85px] lg:-bottom-[85px] xl:-bottom-[50px] 1xl:-bottom-[60px] 2xl:-bottom-[150px] 4xl:-bottom-[120px] left-1/2 -translate-x-1/2 z-10"
          >
            <EnfinitySecondaryBtn
              name="Get paid faster with EnfiniPay"
              className="shadow-[0_4px_6px_rgba(0,0,0,0.1)] text-white"
              style={{
                background: "linear-gradient(to right, #00A656, #127E00)",
              }}
            />
          </a>
        </motion.div>
      </div>
    </div>
  );
}
